import type { LucideIcon } from 'lucide-react'
import { useRef, type ButtonHTMLAttributes, type MouseEvent, type PointerEvent, type ReactNode, type Ref } from 'react'
import { cn } from '../lib/cn'
import { useHotkey, type Hotkey } from '../lib/hotkeys'
import { useLink } from './context'
import { Kbd } from './Kbd'
import { Spinner } from './Spinner'
import { toast } from './Toast'
import { Tooltip } from './Tooltip'
import type { Size } from './types'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-solid' | 'link'

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Standard 'secondary'. `primary` höchstens einmal je Ansicht. */
  variant?: ButtonVariant
  size?: Size
  /** vor dem Text, 16 px */
  icon?: LucideIcon
  iconEnd?: LucideIcon
  /** Spinner ersetzt das Icon, Breite bleibt, aria-busy, Klicks werden ignoriert */
  loading?: boolean
  /** für Screenreader: „Wird gebucht …“ */
  loadingLabel?: string
  /** zeigt <Kbd> am Ende (nur Desktop) und registriert das Kürzel, solange sichtbar und aktiv */
  hotkey?: Hotkey
  fullWidth?: boolean
  /** rendert einen Link in gleicher Optik */
  href?: string
  /** erscheint als Tooltip an der deaktivierten Schaltfläche */
  disabledReason?: string
  ref?: Ref<HTMLButtonElement>
  children: ReactNode
}

const SIZE: Record<Size, string> = {
  sm: 'h-control-sm gap-1 px-2',
  md: 'h-control gap-1.5 px-(--control-px)',
  lg: 'h-control-lg gap-2 px-4',
}

const ENABLED: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-on-accent accent-edge hover:bg-accent-hover active:bg-accent-pressed',
  secondary: 'border border-line-strong bg-surface text-fg shadow-xs hover:bg-hover active:bg-pressed',
  ghost: 'text-fg hover:bg-hover active:bg-pressed',
  danger:
    'border border-danger-line bg-surface text-danger-fg shadow-xs hover:bg-danger-subtle active:bg-danger-subtle',
  'danger-solid': 'bg-danger text-on-status hover:opacity-90 active:opacity-80',
  link: 'text-fg-accent underline-offset-2 hover:underline',
}

const DISABLED: Record<ButtonVariant, string> = {
  primary: 'bg-pressed text-fg-disabled',
  secondary: 'border border-line bg-surface text-fg-disabled',
  ghost: 'text-fg-disabled',
  danger: 'border border-line bg-surface text-fg-disabled',
  'danger-solid': 'bg-pressed text-fg-disabled',
  link: 'text-fg-disabled',
}

export const buttonClass = (variant: ButtonVariant, size: Size, disabled: boolean, fullWidth?: boolean): string =>
  cn(
    'hit-area inline-flex shrink-0 items-center justify-center rounded-sm text-dense font-medium whitespace-nowrap select-none touch:text-touch',
    'transition-colors duration-(--duration-instant) ease-snap',
    variant === 'link' ? 'h-auto min-w-0 gap-1 rounded-xs px-0' : cn('min-w-16', SIZE[size]),
    disabled ? cn(DISABLED[variant], 'cursor-not-allowed') : ENABLED[variant],
    fullWidth && 'w-full',
  )

/**
 * Was eine Schaltfläche als Link behält: Kennung für Tests, Beschriftung und Zustand für Hilfstechnik, Tooltip –
 * also `data-*`, `aria-*`, `title`, `id`, `role`, `tabIndex`. Alles, was nur ein <button> kennt (name, value, form,
 * Ereignisse mit dem Typ der Schaltfläche), bleibt weg.
 */
export function linkSafe(rest: Record<string, unknown>): Record<string, unknown> {
  const kept: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(rest)) {
    if (key.startsWith('data-') || key.startsWith('aria-') || key === 'title' || key === 'id' || key === 'role' || key === 'tabIndex') kept[key] = value
  }
  return kept
}

/** Schaltfläche. Die Beschriftung nennt die Handlung („Wareneingang buchen“), nie „OK“. */
export function Button({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  iconEnd: IconEnd,
  loading = false,
  loadingLabel,
  hotkey,
  fullWidth,
  href,
  disabled = false,
  disabledReason,
  className,
  children,
  onClick,
  onPointerDown,
  type = 'button',
  ref,
  ...rest
}: ButtonProps) {
  const Link = useLink()
  const localRef = useRef<HTMLButtonElement | null>(null)
  const inactive = disabled || loading
  // Finger und Stift kennen kein Hover: Ein Tipp auf eine gesperrte Schaltfläche nennt den Grund (§6.2)
  const lastPointer = useRef('')

  useHotkey(hotkey, () => localRef.current?.click(), { enabled: !inactive && !href })

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (inactive) {
      event.preventDefault()
      if (disabled && disabledReason && (lastPointer.current === 'touch' || lastPointer.current === 'pen')) {
        toast.show({ id: 'disabled-reason', title: disabledReason, tone: 'info' })
      }
      return
    }
    onClick?.(event)
  }
  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
    lastPointer.current = event.pointerType
    onPointerDown?.(event)
  }

  const iconClass = cn('shrink-0', size === 'lg' ? 'size-5' : 'size-4 touch:size-5')
  const content = (
    <>
      {loading && Icon ? (
        <Spinner className={iconClass} />
      ) : (
        Icon && <Icon className={iconClass} strokeWidth={1.75} aria-hidden />
      )}
      <span className={cn('truncate-line', loading && !Icon && 'opacity-0')}>{children}</span>
      {loading && !Icon && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner className={iconClass} />
        </span>
      )}
      {IconEnd && <IconEnd className={iconClass} strokeWidth={1.75} aria-hidden />}
      {hotkey && variant !== 'link' && (
        <Kbd hotkey={hotkey} variant="plain" className="ml-1 hidden md:inline-flex touch:hidden" />
      )}
      {loading && <span className="sr-only">{loadingLabel ?? 'Wird ausgeführt …'}</span>}
    </>
  )
  const classes = cn(buttonClass(variant, size, disabled, fullWidth), loading && 'cursor-progress', className)

  if (href && !inactive) {
    return (
      <Link {...linkSafe(rest)} href={href} className={classes} data-ui="button" data-variant={variant}>
        {content}
      </Link>
    )
  }

  // Mit Begründung bleibt die Schaltfläche fokussierbar (aria-disabled), damit der Tooltip erreichbar ist.
  const softDisabled = disabled && Boolean(disabledReason)
  const button = (
    <button
      {...rest}
      ref={(node) => {
        localRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      }}
      type={type}
      className={classes}
      disabled={disabled && !softDisabled}
      aria-disabled={softDisabled || undefined}
      aria-busy={loading || undefined}
      data-ui="button"
      data-variant={variant}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
    >
      {content}
    </button>
  )
  return softDisabled ? <Tooltip content={disabledReason}>{button}</Tooltip> : button
}
